/*
 * -----------------------------------------------------------------------
 *  <copyright file="study-products-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';

import { useBoolean, useGridCells, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import DeleteProductDialog from 'views/components/admin/studies/product-dialogs/delete-product-dialog';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

interface StudyProductsGridInterface {
    study: StudyFields;
    canEdit: boolean;
}

const StudyProductsGrid = ({ study, canEdit }: StudyProductsGridInterface) => {
    const { t } = useTranslation();
    const [currentProduct, setCurrentProduct] = useState(null);
    const showDeleteProductDialog = useBoolean(false);
    const [loadData, { data, loading, error }] = useLazyQuery(GET_ORGANISATION_PROGRAM_PRODUCT);
    const { MaxLengthCell, YesNoCell } = useGridCells();

    useEffect(() => {
        if (study) loadData();
    }, [study]);

    const actions = (item: any) => {
        if (!item._isProgram)
            return [
                {
                    label: 'admin:unassignProduct',
                    icon: ['fal', 'link-slash'],
                    onClick: () => {
                        setCurrentProduct(item);
                        showDeleteProductDialog.setTrue();
                    },
                    enabled: canEdit,
                },
            ];
        return null;
    };

    const getAvailableProducts = useMemo((): any[] => {
        // Items can be duplicated
        let availableStudyProducts = data?.organisation?.products
            .filter((product: any) => study.productsIds.includes(product.id))
            .map((product: any) => ({
                ...product,
                _isProgram: false,
                referenceNumber: product.referenceNumber || '',
                activeIngredients: product.activeIngredients || '',
                registrationNumber: product.registrationNumber || '',
            }));

        const availablePrograms = data?.organisation?.programs.filter((program: any) =>
            study.programsIds.includes(program.id),
        );
        const availableProgramProductsIds =
            availablePrograms?.reduce((result: any, program: any) => result.concat(program.productIds), []) || [];
        const availableProgramProducts = data?.organisation?.products
            .filter((product: any) => availableProgramProductsIds.includes(product.id))
            .map((product: any) => ({ ...product, _isProgram: true }));
        availableStudyProducts = availableStudyProducts?.concat(availableProgramProducts) || [];
        return orderBy(availableStudyProducts, [{ field: 'name', dir: 'asc' }]);
    }, [data]);

    return (
        <>
            <DeleteProductDialog
                study={study}
                currentProduct={currentProduct}
                open={showDeleteProductDialog.value}
                onClose={() => {
                    setCurrentProduct(null);
                    showDeleteProductDialog.setFalse();
                }}
            />
            <ExpandableGrid
                customKey="products-grid"
                id="productsGrid"
                key="productsGrid"
                items={getAvailableProducts}
                rowActions={actions}
                groupable={false}
                showPager
                addScroll
                scrollable="scrollable"
                reorderable
                pageable={false}
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                detail={null}
                columns={[
                    {
                        field: 'name',
                        label: t('admin:product'),
                        id: 'product-name',
                        minWidth: 140,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(70) },
                    },
                    {
                        field: 'registrationNumber',
                        id: 'product-registrationNumber',
                        label: t('admin:registrationNumber'),
                        minWidth: 240,
                        cells: { data: MaxLengthCell(70) },
                    },
                    {
                        field: 'activeIngredients',
                        id: 'product-activeIngredients',
                        label: t('admin:activeIngredients'),
                        minWidth: 220,
                        cells: { data: MaxLengthCell(150) },
                    },
                    {
                        field: '_isProgram',
                        id: 'product-isProgram',
                        label: t('admin:programProduct'),
                        minWidth: 200,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                ]}
            />
        </>
    );
};

export default StudyProductsGrid;
